import type { CollectionFields } from '@dovefiniscono/shared';
import { stableId } from '@dovefiniscono/sync';
import { useCollection, useStore, useSyncUserId } from '@dovefiniscono/sync/react';
import { useCallback, useMemo } from 'react';

export type Preferences = CollectionFields['preferences'];

export const DEFAULT_PREFERENCES: Preferences = {
  restWeekdays: [],
  restDays: [],
  weeklyGoals: [],
  reviewedWeeks: [],
};

/** Single days off allowed in a month, so they stay a rest and not a way round the streak. */
export const REST_DAYS_PER_MONTH = 4;

/**
 * Your preferences: one record per user, with the same id on every device so two devices that
 * create it at once make one.
 */
export function usePreferences() {
  const userId = useSyncUserId();
  const store = useStore();
  const records = useCollection('preferences');
  const id = stableId(`preferences:${userId}`);
  const record = records.find((item) => item.id === id) ?? records[0] ?? null;
  const preferences = useMemo<Preferences>(
    () =>
      record
        ? {
            restWeekdays: record.restWeekdays,
            restDays: record.restDays,
            weeklyGoals: record.weeklyGoals,
            reviewedWeeks: record.reviewedWeeks,
            notifications: record.notifications ?? null,
          }
        : DEFAULT_PREFERENCES,
    [record],
  );
  const save = useCallback(
    (patch: Partial<Preferences>) => {
      if (record) store.update('preferences', record.id, patch);
      else store.create('preferences', { ...DEFAULT_PREFERENCES, ...patch }, id);
    },
    [record, store, id],
  );
  return [preferences, save] as const;
}
